import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const AuthContext = createContext(null);
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export function AuthProvider({ children }) {
  const [user, setUser]                                 = useState(null);
  const [supabaseToken, setSupabaseToken]               = useState(null);
  const [supabaseRefreshToken, setSupabaseRefreshToken] = useState(null);
  const [loading, setLoading]                           = useState(true);
  const [isAdmin, setIsAdmin]                           = useState(false);
  const [isAdminLoading, setIsAdminLoading]             = useState(true);

  // Refs for stable access inside authFetch closures
  const supabaseTokenRef        = useRef(null);
  const supabaseRefreshTokenRef = useRef(null);

  useEffect(() => { supabaseTokenRef.current        = supabaseToken; },        [supabaseToken]);
  useEffect(() => { supabaseRefreshTokenRef.current = supabaseRefreshToken; }, [supabaseRefreshToken]);

  // Restore session from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('tripdash_supabase_token');
    const savedRefr  = localStorage.getItem('tripdash_supabase_refresh_token');
    const savedUser  = localStorage.getItem('tripdash_user');

    if (savedToken && savedUser) {
      try {
        setSupabaseToken(savedToken);
        setUser(JSON.parse(savedUser));
        if (savedRefr) setSupabaseRefreshToken(savedRefr);
        fetch(`${API_BASE_URL}/api/auth/is-admin`, {
          headers: { Authorization: `Bearer ${savedToken}` },
        })
          .then(r => { console.log('[is-admin] status:', r.status); return r.ok ? r.json() : { isAdmin: false }; })
          .then(d => { console.log('[is-admin] result:', d); setIsAdmin(!!d.isAdmin); })
          .catch(e => { console.error('[is-admin] error:', e); setIsAdmin(false); })
          .finally(() => setIsAdminLoading(false));
      } catch {
        localStorage.removeItem('tripdash_supabase_token');
        localStorage.removeItem('tripdash_supabase_refresh_token');
        localStorage.removeItem('tripdash_user');
        setIsAdminLoading(false);
      }
    } else {
      setIsAdminLoading(false);
    }

    // Clean up legacy custom JWT key left over from Phase 2
    localStorage.removeItem('tripdash_token');

    setLoading(false);
  }, []);

  // login() — called after /api/auth/login or /api/auth/register
  const login = (sbAccessToken, sbRefreshToken, userData) => {
    setSupabaseToken(sbAccessToken);
    setSupabaseRefreshToken(sbRefreshToken);
    setUser(userData);
    localStorage.setItem('tripdash_supabase_token', sbAccessToken);
    localStorage.setItem('tripdash_supabase_refresh_token', sbRefreshToken);
    localStorage.setItem('tripdash_user', JSON.stringify(userData));
    setIsAdminLoading(true);
    fetch(`${API_BASE_URL}/api/auth/is-admin`, {
      headers: { Authorization: `Bearer ${sbAccessToken}` },
    })
      .then(r => r.ok ? r.json() : { isAdmin: false })
      .then(d => setIsAdmin(!!d.isAdmin))
      .catch(() => setIsAdmin(false))
      .finally(() => setIsAdminLoading(false));
  };

  const logout = () => {
    setSupabaseToken(null);
    setSupabaseRefreshToken(null);
    setUser(null);
    setIsAdmin(false);
    localStorage.removeItem('tripdash_supabase_token');
    localStorage.removeItem('tripdash_supabase_refresh_token');
    localStorage.removeItem('tripdash_user');
  };

  // Attempt to refresh an expired Supabase access token.
  // Returns true if successful, false if the session is unrecoverable.
  const _refreshSupabaseToken = async () => {
    const rt = supabaseRefreshTokenRef.current;
    if (!rt) return false;

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) return false;

      const data = await res.json();
      if (!data.supabaseAccessToken) return false;

      setSupabaseToken(data.supabaseAccessToken);
      localStorage.setItem('tripdash_supabase_token', data.supabaseAccessToken);

      if (data.supabaseRefreshToken) {
        setSupabaseRefreshToken(data.supabaseRefreshToken);
        localStorage.setItem('tripdash_supabase_refresh_token', data.supabaseRefreshToken);
      }
      return true;
    } catch {
      return false;
    }
  };

  // authFetch — authenticated wrapper around fetch.
  // On 401/403: attempts one automatic Supabase token refresh before logging out.
  const authFetch = async (url, options = {}, _isRetry = false) => {
    const token = supabaseTokenRef.current;

    const isFormData = options.body instanceof FormData;
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const finalUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    const response = await fetch(finalUrl, { ...options, headers });

    // Let feature-gating 403/429 responses pass through to the caller untouched.
    // Pages handle these themselves — no global toast/banner.
    if (response.status === 403 || response.status === 429) {
      const cloned = response.clone();
      try {
        const body = await cloned.json();
        if (body.error === 'upgrade_required' || body.error === 'usage_cap_reached') {
          return new Response(JSON.stringify(body), {
            status: response.status,
            headers: { 'Content-Type': 'application/json' },
          });
        }
      } catch {}
    }

    if ((response.status === 401 || response.status === 403) && !_isRetry && supabaseRefreshTokenRef.current) {
      const refreshed = await _refreshSupabaseToken();
      if (refreshed) return authFetch(url, options, true);
    }

    if (response.status === 401 || response.status === 403) {
      logout();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    return response;
  };

  return (
    <AuthContext.Provider value={{ user, supabaseToken, login, logout, authFetch, loading, isAdmin, isAdminLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
